import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, AreaChart, Area,
} from 'recharts'

type Tab = 'overview' | 'investigation' | 'contribution' | 'correlation' | 'navdrawdown' | 'histogram' | 'dendrogram' | 'stresstest' | 'pmfg'

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Risk Metrics' },
  { key: 'investigation', label: 'Fund Investigation' },
  { key: 'contribution', label: 'Risk Contribution' },
  { key: 'correlation', label: 'Correlations' },
  { key: 'navdrawdown', label: 'NAV & Drawdown' },
  { key: 'histogram', label: 'Distribution' },
  { key: 'dendrogram', label: 'Dendrogram' },
  { key: 'stresstest', label: 'Stress Test' },
  { key: 'pmfg', label: 'PMFG Network' },
]

const fmtPct = (v: any) => v != null ? `${(v * 100).toFixed(2)}%` : '-'
const fmtNum = (v: any, d = 2) => v != null ? Number(v).toFixed(d) : '-'
const fmtMo = (v: any) => v != null ? `${(v * 100).toFixed(2)}%` : '-'

/* ── Traffic light for risk signals ── */
function Signal({ value, thresholds, invert }: { value: any; thresholds: [number, number]; invert?: boolean }) {
  if (value == null) return <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300" />
  let color = 'bg-green-500'
  if (invert) {
    if (value < thresholds[0]) color = 'bg-red-500'
    else if (value < thresholds[1]) color = 'bg-yellow-500'
  } else {
    if (value > thresholds[1]) color = 'bg-red-500'
    else if (value > thresholds[0]) color = 'bg-yellow-500'
  }
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
}

/* ── Sort helpers ── */
type SortDir = 'asc' | 'desc'
function SortTh({ label, sortKey, current, dir, onSort, className = '' }: {
  label: string; sortKey: string; current: string; dir: SortDir; onSort: (k: string) => void; className?: string
}) {
  return (
    <th className={`cursor-pointer select-none ${className}`} onClick={() => onSort(sortKey)}>
      {label} {current === sortKey ? (dir === 'asc' ? '▲' : '▼') : ''}
    </th>
  )
}

export default function RiskDashboard() {
  const [tab, setTab] = useState<Tab>('overview')
  const [months, setMonths] = useState(36)
  const [selectedFund, setSelectedFund] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState('weight')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [corrMode, setCorrMode] = useState<'returns' | 'drawdown'>('returns')

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['risk-dashboard', months],
    queryFn: () => api.get(`/risk/dashboard?months=${months}`).then(r => r.data),
  })

  const { data: riskContrib } = useQuery({
    queryKey: ['risk-contribution', months],
    queryFn: () => api.get(`/risk/risk-contribution?months=${months}`).then(r => r.data),
    enabled: tab === 'contribution',
  })

  const { data: corrData } = useQuery({
    queryKey: ['risk-correlation', months],
    queryFn: () => api.get(`/risk/correlation?months=${months}`).then(r => r.data),
    enabled: tab === 'correlation',
  })

  const { data: portSeries } = useQuery({
    queryKey: ['portfolio-series', months],
    queryFn: () => api.get(`/risk/portfolio-series?months=${months}`).then(r => r.data),
    enabled: tab === 'navdrawdown' || tab === 'histogram',
  })

  const { data: fundMetrics } = useQuery({
    queryKey: ['fund-risk-metrics', selectedFund, months],
    queryFn: () => api.get(`/risk/metrics/${selectedFund}?months=${months}`).then(r => r.data),
    enabled: tab === 'investigation' && selectedFund != null,
  })

  const { data: dendrogramData } = useQuery({
    queryKey: ['risk-dendrogram', months],
    queryFn: () => api.get(`/risk/dendrogram?months=${months}`).then(r => r.data),
    enabled: tab === 'dendrogram',
  })

  const { data: stressData } = useQuery({
    queryKey: ['risk-stress-test', months],
    queryFn: () => api.get(`/risk/stress-test?months=${months}`).then(r => r.data),
    enabled: tab === 'stresstest',
  })

  const { data: pmfgData } = useQuery({
    queryKey: ['risk-pmfg', months],
    queryFn: () => api.get(`/risk/pmfg?months=${months}`).then(r => r.data),
    enabled: tab === 'pmfg',
  })

  const sortedHoldings = useMemo(() => {
    const h = data?.holdings || []
    return [...h].sort((a: any, b: any) => {
      const av = a[sortKey] ?? -Infinity
      const bv = b[sortKey] ?? -Infinity
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
    })
  }, [data?.holdings, sortKey, sortDir])

  const p = data?.portfolio || {}

  if (isLoading) {
    return <div className="flex justify-center p-8"><span className="spinner" /></div>
  }

  /* ── Contribution chart data ── */
  const volContribData = riskContrib?.funds?.map((f: string, i: number) => ({
    name: f.length > 25 ? f.slice(0, 22) + '...' : f, fullName: f,
    contribution: riskContrib.vol_contributions[i] * 100,
  })) || []

  const ddContribData = riskContrib?.funds?.map((f: string, i: number) => ({
    name: f.length > 25 ? f.slice(0, 22) + '...' : f, fullName: f,
    contribution: riskContrib.dd_contributions[i] * 100,
  })) || []

  const marginalData = riskContrib?.marginal || []

  /* ── Correlation heatmap helper ── */
  const corrMatrix = corrMode === 'returns' ? corrData?.returns_corr : corrData?.drawdown_corr
  const corrFunds = corrData?.funds || []

  function corrColor(v: number): string {
    if (v >= 0.7) return 'bg-red-600 text-white'
    if (v >= 0.4) return 'bg-red-300'
    if (v >= 0.1) return 'bg-orange-200'
    if (v >= -0.1) return 'bg-gray-100'
    if (v >= -0.4) return 'bg-blue-200'
    return 'bg-blue-500 text-white'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Risk Dashboard</h1>
          <p className="text-muted-foreground">Portfolio risk monitoring and analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Lookback:</label>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="h-8 px-2 border border-input rounded-md bg-background text-sm">
            {[12, 24, 36, 48, 60, 84, 120].map(m => <option key={m} value={m}>{m}m</option>)}
          </select>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card label="Total Value" value={formatCurrency(data?.total_value)} />
        <Card label="Holdings" value={data?.holdings_count || 0} />
        <Card label="Risk Flags" value={data?.risk_flags_count || 0} color={data?.risk_flags_count > 0 ? 'text-red-600' : 'text-green-600'} />
        <Card label="Port. Sharpe" value={fmtNum(p.sharpe_ratio)} />
        <Card label="Port. Sortino" value={fmtNum(p.sortino_ratio)} />
        <Card label="Port. Vol" value={fmtPct(p.ann_volatility)} />
        <Card label="Port. Max DD" value={fmtPct(p.max_drawdown)} color="text-red-600" />
        <Card label="Port. VaR 95" value={fmtMo(p.var_95)} color="text-red-600" />
      </div>

      {/* ── Portfolio Stats Row ── */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card label="Ann. Return" value={fmtPct(p.ann_return)} color={p.ann_return >= 0 ? 'text-green-600' : 'text-red-600'} />
        <Card label="Calmar" value={fmtNum(p.calmar_ratio)} />
        <Card label="Skewness" value={fmtNum(p.skewness)} />
        <Card label="Kurtosis" value={fmtNum(p.kurtosis)} />
        <Card label="CVaR 95" value={fmtMo(p.cvar_95)} color="text-red-600" />
        <Card label="Avg Corr" value={fmtNum(p.avg_pairwise_correlation)} />
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════ OVERVIEW TAB ═══════ */}
      {tab === 'overview' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <h2 className="font-semibold">Holdings Risk Metrics ({months}m lookback)</h2>
            <span className="text-xs text-muted-foreground">{sortedHoldings.length} funds</span>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <SortTh label="Fund" sortKey="fund_name" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortTh label="Value" sortKey="value" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Weight" sortKey="weight" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Return" sortKey="ann_return" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Vol" sortKey="ann_volatility" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Sharpe" sortKey="sharpe_ratio" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Sortino" sortKey="sortino_ratio" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Calmar" sortKey="calmar_ratio" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Max DD" sortKey="max_drawdown" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Cur DD" sortKey="current_drawdown" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Skew" sortKey="skewness" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Kurt" sortKey="kurtosis" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="VaR 95" sortKey="var_95" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <SortTh label="Win%" sortKey="win_rate" current={sortKey} dir={sortDir} onSort={toggleSort} className="text-right" />
                  <th className="text-center">Flags</th>
                </tr>
              </thead>
              <tbody>
                {sortedHoldings.map((h: any) => (
                  <tr key={h.fund_id} className="hover:bg-muted/50 cursor-pointer" onClick={() => { setSelectedFund(h.fund_id); setTab('investigation') }}>
                    <td className="font-medium max-w-[200px] truncate" title={h.fund_name}>{h.fund_name}</td>
                    <td className="text-right font-mono">{formatCurrency(h.value)}</td>
                    <td className="text-right font-mono">{fmtPct(h.weight)}</td>
                    <td className={`text-right font-mono ${h.ann_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtPct(h.ann_return)}</td>
                    <td className="text-right font-mono">{fmtPct(h.ann_volatility)}</td>
                    <td className="text-right font-mono">{fmtNum(h.sharpe_ratio)}</td>
                    <td className="text-right font-mono">{fmtNum(h.sortino_ratio)}</td>
                    <td className="text-right font-mono">{fmtNum(h.calmar_ratio)}</td>
                    <td className="text-right font-mono text-red-600">{fmtPct(h.max_drawdown)}</td>
                    <td className="text-right font-mono">{fmtPct(h.current_drawdown)}</td>
                    <td className="text-right font-mono">{fmtNum(h.skewness)}</td>
                    <td className="text-right font-mono">{fmtNum(h.kurtosis)}</td>
                    <td className="text-right font-mono text-red-600">{fmtMo(h.var_95)}</td>
                    <td className="text-right font-mono">{h.win_rate != null ? `${(h.win_rate * 100).toFixed(0)}%` : '-'}</td>
                    <td className="text-center">
                      {h.abnormal_return_flag ? <span className="text-red-600 font-bold" title={h.monitoring_comment || 'Abnormal return'}>!</span> : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════ FUND INVESTIGATION TAB ═══════ */}
      {tab === 'investigation' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Select Fund:</label>
            <select value={selectedFund ?? ''} onChange={e => setSelectedFund(Number(e.target.value) || null)}
              className="h-9 px-3 border border-input rounded-md bg-background text-sm min-w-[300px]">
              <option value="">-- Choose a fund --</option>
              {(data?.holdings || []).map((h: any) => (
                <option key={h.fund_id} value={h.fund_id}>{h.fund_name}</option>
              ))}
            </select>
          </div>

          {selectedFund && fundMetrics && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">
                {data?.holdings?.find((h: any) => h.fund_id === selectedFund)?.fund_name || `Fund #${selectedFund}`}
              </h2>

              {/* Signal overview */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Risk Signals</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <MetricRow label="Sharpe Ratio" value={fmtNum(fundMetrics.sharpe_ratio)} signal={<Signal value={fundMetrics.sharpe_ratio} thresholds={[0.5, 1.0]} invert />} />
                  <MetricRow label="Sortino Ratio" value={fmtNum(fundMetrics.sortino_ratio)} signal={<Signal value={fundMetrics.sortino_ratio} thresholds={[0.7, 1.5]} invert />} />
                  <MetricRow label="Max Drawdown" value={fmtPct(fundMetrics.max_drawdown)} signal={<Signal value={fundMetrics.max_drawdown} thresholds={[-0.15, -0.05]} />} />
                  <MetricRow label="Current DD" value={fmtPct(fundMetrics.current_drawdown)} signal={<Signal value={fundMetrics.current_drawdown} thresholds={[-0.10, -0.03]} />} />
                  <MetricRow label="Skewness" value={fmtNum(fundMetrics.skewness)} signal={<Signal value={fundMetrics.skewness} thresholds={[-0.5, 0]} invert />} />
                  <MetricRow label="Kurtosis" value={fmtNum(fundMetrics.kurtosis)} signal={<Signal value={fundMetrics.kurtosis} thresholds={[3, 6]} />} />
                  <MetricRow label="VaR 95" value={fmtMo(fundMetrics.var_95)} signal={<Signal value={fundMetrics.var_95} thresholds={[-0.05, -0.02]} />} />
                  <MetricRow label="CVaR 95" value={fmtMo(fundMetrics.cvar_95)} signal={<Signal value={fundMetrics.cvar_95} thresholds={[-0.08, -0.03]} />} />
                </div>
              </div>

              {/* Performance metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Performance</h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <KVRow label="Ann. Return" value={fmtPct(fundMetrics.ann_return)} />
                      <KVRow label="Ann. Volatility" value={fmtPct(fundMetrics.ann_volatility)} />
                      <KVRow label="Sharpe Ratio" value={fmtNum(fundMetrics.sharpe_ratio)} />
                      <KVRow label="Sortino Ratio" value={fmtNum(fundMetrics.sortino_ratio)} />
                      <KVRow label="Calmar Ratio" value={fmtNum(fundMetrics.calmar_ratio)} />
                      <KVRow label="Win Rate" value={fundMetrics.win_rate != null ? `${(fundMetrics.win_rate * 100).toFixed(1)}%` : '-'} />
                      <KVRow label="Best Month" value={fmtMo(fundMetrics.best_month)} />
                      <KVRow label="Worst Month" value={fmtMo(fundMetrics.worst_month)} />
                      <KVRow label="Avg Positive" value={fmtMo(fundMetrics.avg_positive)} />
                      <KVRow label="Avg Negative" value={fmtMo(fundMetrics.avg_negative)} />
                      <KVRow label="Positive Months" value={fundMetrics.positive_months} />
                      <KVRow label="Negative Months" value={fundMetrics.negative_months} />
                    </tbody>
                  </table>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Risk Metrics</h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <KVRow label="Max Drawdown" value={fmtPct(fundMetrics.max_drawdown)} />
                      <KVRow label="Current Drawdown" value={fmtPct(fundMetrics.current_drawdown)} />
                      <KVRow label="Norm. Max DD" value={fmtNum(fundMetrics.norm_max_dd)} />
                      <KVRow label="Norm. Cur DD" value={fmtNum(fundMetrics.norm_cur_dd)} />
                      <KVRow label="DD Days Ratio" value={fmtPct(fundMetrics.dd_days_ratio)} />
                      <KVRow label="Cur DD Length" value={`${fundMetrics.cur_dd_length || 0}m`} />
                      <KVRow label="Skewness" value={fmtNum(fundMetrics.skewness)} />
                      <KVRow label="Kurtosis" value={fmtNum(fundMetrics.kurtosis)} />
                      <KVRow label="VaR 95" value={fmtMo(fundMetrics.var_95)} />
                      <KVRow label="VaR 99" value={fmtMo(fundMetrics.var_99)} />
                      <KVRow label="CVaR 95" value={fmtMo(fundMetrics.cvar_95)} />
                      <KVRow label="CVaR 99" value={fmtMo(fundMetrics.cvar_99)} />
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Monitoring info */}
              {(() => {
                const h = data?.holdings?.find((x: any) => x.fund_id === selectedFund)
                if (!h?.target_sharpe && !h?.aum_change_6m && !h?.monitoring_comment) return null
                return (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">Monitoring</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {h.target_sharpe != null && <MetricRow label="Target Sharpe" value={fmtNum(h.target_sharpe)} />}
                      {h.aum_change_6m != null && <MetricRow label="6M AUM Change" value={fmtPct(h.aum_change_6m)} />}
                      {h.abnormal_return_flag != null && <MetricRow label="Abnormal Return" value={h.abnormal_return_flag ? 'YES' : 'No'} />}
                      {h.monitoring_comment && <div className="col-span-2 md:col-span-4"><span className="text-muted-foreground">Comment:</span> {h.monitoring_comment}</div>}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
          {selectedFund && !fundMetrics && <div className="text-center p-8"><span className="spinner" /></div>}
          {!selectedFund && <div className="text-center text-muted-foreground p-8">Select a fund from the dropdown above or click a row in the Risk Metrics tab</div>}
        </div>
      )}

      {/* ═══════ RISK CONTRIBUTION TAB ═══════ */}
      {tab === 'contribution' && (
        <div className="space-y-6">
          {/* Volatility contribution */}
          {volContribData.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Volatility Contribution (%)</h2>
              <p className="text-xs text-muted-foreground mb-4">Each fund's marginal contribution to total portfolio volatility</p>
              <ResponsiveContainer width="100%" height={Math.max(300, volContribData.length * 32)}>
                <BarChart data={volContribData} layout="vertical" margin={{ left: 140, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v.toFixed(1)}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip content={({ payload }) => {
                    if (!payload?.length) return null
                    const d = payload[0].payload
                    return <div className="bg-card border border-border rounded p-2 text-xs shadow-lg"><div className="font-semibold">{d.fullName}</div><div>Contribution: {d.contribution.toFixed(2)}%</div></div>
                  }} />
                  <Bar dataKey="contribution" fill="hsl(217, 91%, 50%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Max DD contribution */}
          {ddContribData.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Max Drawdown Contribution</h2>
              <p className="text-xs text-muted-foreground mb-4">Leave-one-out impact: how portfolio max DD changes when fund is removed</p>
              <ResponsiveContainer width="100%" height={Math.max(300, ddContribData.length * 32)}>
                <BarChart data={ddContribData} layout="vertical" margin={{ left: 140, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v.toFixed(2)}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip content={({ payload }) => {
                    if (!payload?.length) return null
                    const d = payload[0].payload
                    return <div className="bg-card border border-border rounded p-2 text-xs shadow-lg"><div className="font-semibold">{d.fullName}</div><div>DD Impact: {d.contribution.toFixed(3)}%</div></div>
                  }} />
                  <Bar dataKey="contribution" fill="hsl(0, 70%, 50%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Marginal diversification impact table */}
          {marginalData.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold">Marginal Diversification Impact</h2>
                <p className="text-xs text-muted-foreground mt-1">Change in portfolio metrics when each fund is excluded (negative = fund helps)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Fund</th>
                      <th className="text-right">Perf Impact</th>
                      <th className="text-right">Vol Impact</th>
                      <th className="text-right">Sharpe Impact</th>
                      <th className="text-right">DD Impact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marginalData.map((m: any) => (
                      <tr key={m.fund}>
                        <td className="font-medium max-w-[200px] truncate" title={m.fund}>{m.fund}</td>
                        <td className={`text-right font-mono ${m.perf_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtPct(m.perf_impact)}</td>
                        <td className={`text-right font-mono ${m.vol_impact <= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtPct(m.vol_impact)}</td>
                        <td className={`text-right font-mono ${m.sharpe_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtNum(m.sharpe_impact, 3)}</td>
                        <td className={`text-right font-mono ${m.dd_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtPct(m.dd_impact)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {volContribData.length === 0 && <div className="text-center text-muted-foreground p-8">No risk contribution data available</div>}
        </div>
      )}

      {/* ═══════ CORRELATION TAB ═══════ */}
      {tab === 'correlation' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setCorrMode('returns')}
              className={`px-3 py-1.5 text-sm rounded-md ${corrMode === 'returns' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              Returns Correlation
            </button>
            <button onClick={() => setCorrMode('drawdown')}
              className={`px-3 py-1.5 text-sm rounded-md ${corrMode === 'drawdown' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              Drawdown Correlation
            </button>
          </div>

          {corrMatrix && corrFunds.length > 0 ? (
            <div className="bg-card border border-border rounded-lg p-4 overflow-x-auto">
              <h2 className="text-lg font-semibold mb-3">{corrMode === 'returns' ? 'Returns' : 'Drawdown'} Correlation Matrix</h2>
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="p-1 sticky left-0 bg-card z-10" />
                    {corrFunds.map((f: string, i: number) => (
                      <th key={i} className="p-1 text-center writing-mode-vertical min-w-[28px]" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', maxHeight: 120 }}>
                        <span className="text-[10px]">{f.length > 15 ? f.slice(0, 12) + '...' : f}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {corrFunds.map((f: string, i: number) => (
                    <tr key={i}>
                      <td className="p-1 font-medium text-[10px] max-w-[120px] truncate sticky left-0 bg-card z-10" title={f}>
                        {f.length > 18 ? f.slice(0, 15) + '...' : f}
                      </td>
                      {corrMatrix[i].map((v: number, j: number) => (
                        <td key={j} className={`p-1 text-center text-[10px] font-mono border border-border/30 ${i === j ? 'bg-gray-200' : corrColor(v)}`}>
                          {i === j ? '1.00' : v.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex gap-2 mt-3 text-[10px] text-muted-foreground">
                <span className="inline-block w-3 h-3 bg-blue-500 rounded" /> &lt; -0.4
                <span className="inline-block w-3 h-3 bg-blue-200 rounded" /> -0.4 to -0.1
                <span className="inline-block w-3 h-3 bg-gray-100 border rounded" /> -0.1 to 0.1
                <span className="inline-block w-3 h-3 bg-orange-200 rounded" /> 0.1 to 0.4
                <span className="inline-block w-3 h-3 bg-red-300 rounded" /> 0.4 to 0.7
                <span className="inline-block w-3 h-3 bg-red-600 rounded" /> &gt; 0.7
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">Loading correlation data...</div>
          )}
        </div>
      )}

      {/* ═══════ NAV & DRAWDOWN TAB ═══════ */}
      {tab === 'navdrawdown' && (
        <div className="space-y-6">
          {portSeries?.nav_series?.length > 0 ? (
            <>
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Portfolio NAV</h2>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={portSeries.nav_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" tickFormatter={(d: string) => d.slice(0, 7)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => v.toFixed(3)} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                      formatter={(v: number) => v.toFixed(4)} labelFormatter={(l: string) => l.slice(0, 10)} />
                    <Line type="monotone" dataKey="nav" stroke="hsl(217, 91%, 50%)" strokeWidth={2} dot={false} name="Portfolio" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Drawdown</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={portSeries.nav_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" tickFormatter={(d: string) => d.slice(0, 7)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} domain={['auto', 0]} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', fontSize: 12 }}
                      formatter={(v: number) => `${(v * 100).toFixed(2)}%`} labelFormatter={(l: string) => l.slice(0, 10)} />
                    <Area type="monotone" dataKey="drawdown" stroke="hsl(0, 70%, 50%)" fill="hsl(0, 70%, 50%)" fillOpacity={0.2} strokeWidth={1.5} name="Drawdown" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground p-8">No portfolio series data available</div>
          )}
        </div>
      )}

      {/* ═══════ HISTOGRAM TAB ═══════ */}
      {tab === 'histogram' && (
        <div className="space-y-6">
          {portSeries?.histogram?.length > 0 ? (
            <>
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">Monthly Return Distribution</h2>
                <p className="text-xs text-muted-foreground mb-4">Portfolio monthly returns over the last {months} months</p>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={portSeries.histogram.map((h: any) => ({
                    range: `${(h.bin_start * 100).toFixed(1)}%`, count: h.count,
                    bin_start: h.bin_start, bin_end: h.bin_end,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                    <XAxis dataKey="range" tick={{ fontSize: 9 }} interval={Math.max(0, Math.floor(portSeries.histogram.length / 10))} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={({ payload }) => {
                      if (!payload?.length) return null
                      const d = payload[0].payload
                      return <div className="bg-card border border-border rounded p-2 text-xs shadow-lg"><div>Range: {(d.bin_start * 100).toFixed(2)}% to {(d.bin_end * 100).toFixed(2)}%</div><div className="font-semibold">{d.count} months</div></div>
                    }} />
                    <Bar dataKey="count" fill="hsl(217, 91%, 50%)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Distribution stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card label="Best Month" value={fmtMo(p.best_month)} color="text-green-600" />
                <Card label="Worst Month" value={fmtMo(p.worst_month)} color="text-red-600" />
                <Card label="Avg Positive" value={fmtMo(p.avg_positive)} />
                <Card label="Avg Negative" value={fmtMo(p.avg_negative)} />
                <Card label="VaR 95" value={fmtMo(p.var_95)} color="text-red-600" />
                <Card label="VaR 99" value={fmtMo(p.var_99)} color="text-red-600" />
                <Card label="CVaR 95" value={fmtMo(p.cvar_95)} color="text-red-600" />
                <Card label="CVaR 99" value={fmtMo(p.cvar_99)} color="text-red-600" />
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground p-8">No histogram data available</div>
          )}
        </div>
      )}

      {/* ======= DENDROGRAM TAB ======= */}
      {tab === 'dendrogram' && (
        <div className="space-y-6">
          {dendrogramData?.tree ? (
            <>
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">Hierarchical Clustering</h2>
                <p className="text-xs text-muted-foreground mb-4">
                  Funds clustered by return correlation distance (Ward method). Lower distance = more similar return profiles.
                </p>
                <DendrogramTree node={dendrogramData.tree} depth={0} />
              </div>

              {dendrogramData.linkage?.length > 0 && (
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-border">
                    <h2 className="font-semibold">Linkage Steps</h2>
                    <p className="text-xs text-muted-foreground mt-1">Merging order from most similar to least similar pairs</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table text-xs">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Cluster 1</th>
                          <th>Cluster 2</th>
                          <th className="text-right">Distance</th>
                          <th className="text-right">Members</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dendrogramData.linkage.map((row: any, i: number) => {
                          const funds = dendrogramData.funds || []
                          const label = (id: number) => id < funds.length ? funds[id] : `Cluster ${id - funds.length + 1}`
                          return (
                            <tr key={i}>
                              <td className="font-mono">{i + 1}</td>
                              <td className="max-w-[200px] truncate" title={label(row.cluster1)}>{label(row.cluster1)}</td>
                              <td className="max-w-[200px] truncate" title={label(row.cluster2)}>{label(row.cluster2)}</td>
                              <td className="text-right font-mono">{row.distance.toFixed(4)}</td>
                              <td className="text-right font-mono">{row.count}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted-foreground p-8">
              {dendrogramData ? 'Need at least 3 funds for clustering' : 'Loading dendrogram data...'}
            </div>
          )}
        </div>
      )}

      {/* ======= STRESS TEST TAB ======= */}
      {tab === 'stresstest' && (
        <div className="space-y-6">
          {stressData?.crises?.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold">Historical Stress Test</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Portfolio and per-fund cumulative returns during historical crisis periods
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Crisis</th>
                      <th>Period</th>
                      <th className="text-center">Months</th>
                      {(stressData.funds || []).map((f: string) => (
                        <th key={f} className="text-right min-w-[80px]" title={f}>
                          {f.length > 12 ? f.slice(0, 10) + '..' : f}
                        </th>
                      ))}
                      <th className="text-right font-bold min-w-[80px]">Portfolio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stressData.crises.map((c: any) => (
                      <tr key={c.name}>
                        <td className="font-medium whitespace-nowrap">{c.name}</td>
                        <td className="text-muted-foreground whitespace-nowrap text-[10px]">
                          {c.start.slice(0, 7)} to {c.end.slice(0, 7)}
                        </td>
                        <td className="text-center font-mono">{c.months_covered || '-'}</td>
                        {(stressData.funds || []).map((f: string) => {
                          const ret = c.fund_returns?.[f]
                          return (
                            <td key={f} className={`text-right font-mono ${ret != null ? (ret >= 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                              {ret != null ? fmtPct(ret) : '-'}
                            </td>
                          )
                        })}
                        <td className={`text-right font-mono font-bold ${c.portfolio_return != null ? (c.portfolio_return >= 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                          {c.portfolio_return != null ? fmtPct(c.portfolio_return) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground p-8">
              {stressData ? 'No stress test data available' : 'Loading stress test data...'}
            </div>
          )}
        </div>
      )}

      {/* ======= PMFG NETWORK TAB ======= */}
      {tab === 'pmfg' && (
        <div className="space-y-6">
          {pmfgData?.nodes?.length > 0 ? (
            <>
              {/* Summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card label="Nodes" value={pmfgData.nodes.length} />
                <Card label="Edges" value={pmfgData.edges?.length || 0} />
                <Card label="Max Edges (3V-6)" value={pmfgData.max_edges} />
                <Card label="Total Possible" value={pmfgData.total_possible} />
              </div>

              {/* Node degree table */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold">Node Degrees</h2>
                  <p className="text-xs text-muted-foreground mt-1">Sorted by degree (most connected first)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Fund</th>
                        <th className="text-right">Degree</th>
                        <th className="text-right">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...pmfgData.nodes]
                        .sort((a: any, b: any) => (b.degree || 0) - (a.degree || 0))
                        .map((node: any) => (
                          <tr key={node.id}>
                            <td className="font-medium max-w-[250px] truncate" title={node.id}>{node.id}</td>
                            <td className="text-right font-mono">{node.degree}</td>
                            <td className="text-right font-mono">{fmtPct(node.weight)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Edge table */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold">PMFG Edges</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Planar Maximally Filtered Graph edges sorted by correlation (descending)
                  </p>
                </div>
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="data-table text-xs">
                    <thead className="sticky top-0 bg-card">
                      <tr>
                        <th>Source</th>
                        <th>Target</th>
                        <th className="text-right">Correlation</th>
                        <th className="text-right">Distance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pmfgData.edges || []).map((e: any, i: number) => {
                        const corrColor = e.correlation > 0.5 ? 'text-green-600' :
                          e.correlation > 0.2 ? 'text-yellow-600' : 'text-red-600'
                        return (
                          <tr key={i}>
                            <td className="max-w-[200px] truncate" title={e.source}>{e.source}</td>
                            <td className="max-w-[200px] truncate" title={e.target}>{e.target}</td>
                            <td className={`text-right font-mono font-semibold ${corrColor}`}>{fmtNum(e.correlation, 4)}</td>
                            <td className="text-right font-mono">{fmtNum(e.distance, 4)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-muted-foreground p-8">
              {pmfgData ? 'Need at least 3 funds for PMFG network' : 'Loading PMFG data...'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Small helper components ── */
function Card({ label, value, color }: { label: string; value: any; color?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${color || ''}`}>{value}</div>
    </div>
  )
}

function MetricRow({ label, value, signal }: { label: string; value: string; signal?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 font-mono">{signal} {value}</span>
    </div>
  )
}

function KVRow({ label, value }: { label: string; value: any }) {
  return (
    <tr className="border-b border-border/30">
      <td className="py-1 text-muted-foreground">{label}</td>
      <td className="py-1 text-right font-mono">{value}</td>
    </tr>
  )
}

function DendrogramTree({ node, depth }: { node: any; depth: number }) {
  if (!node) return null
  const isLeaf = !node.children
  const indent = depth * 24
  return (
    <div>
      <div className="flex items-center py-0.5 hover:bg-muted/30 rounded" style={{ paddingLeft: indent }}>
        {!isLeaf && <span className="text-muted-foreground mr-1 text-xs">+</span>}
        <span className={`text-sm ${isLeaf ? 'font-medium' : 'text-muted-foreground'}`}>
          {isLeaf ? node.name : `Cluster (d=${node.distance?.toFixed(3) || '?'})`}
        </span>
      </div>
      {node.children?.map((child: any, i: number) => (
        <DendrogramTree key={i} node={child} depth={depth + 1} />
      ))}
    </div>
  )
}
